/**
 * Utilities for converting between different text formats used across the application
 */

// Convert plain text with numbered lists to HTML
export function plainTextToHtml(text: string): string {
  if (!text) return '';
  
  // Normalize line endings and ensure a blank line before each numbered item
  const normalized = text
    .replace(/\r\n/g, '\n')
    // Insert a blank line before every line that starts with a number + dot
    .replace(/(^|\n)(\d+\.\s)/g, (_m, p1, p2) => `${p1}\n\n${p2}`)
    // Remove any leading newlines introduced at the start
    .replace(/^\n+/, '')
    // Collapse runs of 3+ newlines to exactly two
    .replace(/\n{3,}/g, '\n\n');
  
  // Split text into paragraphs
  const paragraphs = normalized.split('\n\n').filter(p => p.trim());
  
  let html = '';
  let inList = false;
  let currentListType = '';
  
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.trim();
    if (!trimmed) continue;
    
    // Check if this is a numbered list item
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)/s);
    const letteredMatch = trimmed.match(/^\s*([a-zA-Z])\.\s+(.+)/s);
    const romanMatch = trimmed.match(/^\s*([ivx]+)\.\s+(.+)/s);
    const subNumberedMatch = trimmed.match(/^(\d+\.[a-zA-Z])\.\s+(.+)/s);
    
    if (numberedMatch || letteredMatch || romanMatch || subNumberedMatch) {
      const listType = numberedMatch ? '1' : letteredMatch ? 'a' : romanMatch ? 'i' : '1';
      const content = numberedMatch?.[2] || letteredMatch?.[2] || romanMatch?.[2] || subNumberedMatch?.[2] || '';
      
      if (!inList || currentListType !== listType) {
        if (inList) html += '</ol>';
        html += `<ol type="${listType}">`;
        inList = true;
        currentListType = listType;
      }
      
      html += `<li>${content}</li>`;
    } else {
      if (inList) {
        html += '</ol>';
        inList = false;
        currentListType = '';
      }
      html += `<p>${trimmed}</p>`;
    }
  }
  
  if (inList) {
    html += '</ol>';
  }
  
  return html;
}

// Convert HTML to plain text format expected by templateDocument
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  
  // Remove HTML tags and convert lists to numbered format
  let text = html
    .replace(/<ol[^>]*>/g, '')
    .replace(/<\/ol>/g, '\n')
    .replace(/<li>/g, '')
    .replace(/<\/li>/g, '\n')
    .replace(/<p>/g, '')
    .replace(/<\/p>/g, '\n\n')
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
    .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newlines
    .trim();
  
  // Re-number the list items
  const lines = text.split('\n').filter(line => line.trim());
  let result = '';
  let counter = 1;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Check if this looks like it should be a list item
    // (heuristic: short line followed by longer content, or starts with common list indicators)
    const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
    const isListItem = line.length < 200 && (
      nextLine.length > line.length || 
      /^(przygotowanie|opracowanie|przeprowadzenie|weryfikacja|analiza|wsparcie|doradztwo)/i.test(line) ||
      /^(preparation|development|conducting|verification|analysis|support|advisory)/i.test(line)
    );
    
    if (isListItem) {
      result += `${counter}. ${line}\n\n`;
      counter++;
    } else {
      result += `${line}\n\n`;
    }
  }
  
  return result.trim();
}

// Enhanced conversion that preserves hierarchical structure
export function htmlToStructuredText(html: string): string {
  if (!html) return '';
  
  // Parse HTML and convert to structured text with proper numbering
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  let result = '';
  let mainCounter = 1;
  let subCounter = 1;
  
  const processNode = (node: Node, level: number = 0): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        result += text;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      
      switch (element.tagName.toLowerCase()) {
        case 'p':
          if (element.textContent?.trim()) {
            result += element.textContent.trim() + '\n\n';
          }
          break;
          
        case 'ol':
          const listType = element.getAttribute('type') || '1';
          let itemCounter = 1;
          
          for (const child of element.children) {
            if (child.tagName.toLowerCase() === 'li') {
              const content = child.textContent?.trim();
              if (content) {
                if (level === 0) {
                  result += `${mainCounter}. ${content}\n\n`;
                  mainCounter++;
                } else {
                  const prefix = level === 1 ? String.fromCharCode(96 + subCounter) : 'i'.repeat(subCounter);
                  result += `${mainCounter - 1}.${prefix}. ${content}\n\n`;
                  subCounter++;
                }
              }
              itemCounter++;
            }
          }
          break;
          
        case 'ul':
          for (const child of element.children) {
            if (child.tagName.toLowerCase() === 'li') {
              const content = child.textContent?.trim();
              if (content) {
                result += `• ${content}\n\n`;
              }
            }
          }
          break;
          
        default:
          for (const child of node.childNodes) {
            processNode(child, level);
          }
          break;
      }
    }
  };
  
  processNode(doc.body);
  
  return result.trim();
}

// Convert generated LLM text to HTML for rich text editor
export function generatedTextToHtml(text: string): string {
  if (!text) return '';
  
  // Normalize line endings and ensure a blank line before each numbered item
  const normalized = text
    .replace(/\r\n/g, '\n')
    // Insert a blank line before every line that starts with a number + dot
    .replace(/(^|\n)(\d+\.\s)/g, (_m, p1, p2) => `${p1}\n\n${p2}`)
    // Remove any leading newlines introduced at the start
    .replace(/^\n+/, '')
    // Collapse runs of 3+ newlines to exactly two
    .replace(/\n{3,}/g, '\n\n');
  
  // Split into paragraphs and process each one
  const paragraphs = normalized.split('\n\n').filter(p => p.trim());
  let html = '';
  let inOrderedList = false;
  
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.trim();
    if (!trimmed) continue;
    
    // Check for numbered list items (1., 2., etc.)
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)/s);
    if (numberedMatch) {
      if (!inOrderedList) {
        html += '<ol>';
        inOrderedList = true;
      }
      html += `<li>${numberedMatch[2]}</li>`;
    } else {
      if (inOrderedList) {
        html += '</ol>';
        inOrderedList = false;
      }
      html += `<p>${trimmed}</p>`;
    }
  }
  
  if (inOrderedList) {
    html += '</ol>';
  }
  
  return html;
}

// For templateDocument compatibility - ensure double newlines between numbered items
export function ensureCompatibleFormat(text: string): string {
  if (!text) return '';
  
  // Ensure numbered list items are separated by double newlines
  return text
    .replace(/(\d+\.\s+[^\n]+)\n(?=\d+\.\s+)/g, '$1\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
} 